import { AlertCircle, CheckCircle, Download, FileJson } from 'lucide-react'
import { useState } from 'react'

interface ExportStatus {
  export_id: string
  status: string
  download_url?: string
  expires_at?: string
  package_size_bytes: number
}

export default function ExportData() {
  const [provider, setProvider] = useState('cloudflare')
  const [loading, setLoading] = useState(false)
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null)
  const [error, setError] = useState('')

  const startExport = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8888'}/api/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider })
      })

      if (!response.ok) {
        throw new Error('Export failed')
      }

      const data = await response.json() as ExportStatus
      if (data) {
        setExportStatus(data)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setLoading(false)
    }
  }

  const downloadPackage = () => {
    if (exportStatus?.download_url) {
      window.location.href = `${import.meta.env.VITE_API_URL || 'http://localhost:8888'}${exportStatus.download_url}`
    }
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) {
      return '0 Bytes'
    }
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '10px' }}>Export Data</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '30px' }}>
        Export all your data to a portable JSON package
      </p>

      {!exportStatus && (
        <div style={{
          background: 'var(--card-bg)',
          padding: '30px',
          borderRadius: '8px',
          border: '1px solid var(--border-color)'
        }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 500 }}>
            Select Provider
          </label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              background: 'var(--body-bg)',
              color: 'var(--text-primary)',
              marginBottom: '20px'
            }}
          >
            <option value="cloudflare">Cloudflare</option>
            <option value="azure">Azure</option>
            <option value="gcp">Google Cloud</option>
            <option value="aws">AWS</option>
          </select>

          <button
            onClick={() => {
              void startExport()
            }}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <FileJson size={20} />
            {loading ? 'Exporting...' : 'Start Export'}
          </button>

          {error && (
            <div style={{
              marginTop: '20px',
              padding: '12px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '4px',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={20} />
              {error}
            </div>
          )}
        </div>
      )}

      {exportStatus && (
        <div style={{
          background: 'var(--card-bg)',
          padding: '30px',
          borderRadius: '8px',
          border: '1px solid var(--border-color)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            padding: '16px',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '4px',
            color: '#22c55e'
          }}>
            <CheckCircle size={24} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>Export Completed</div>
              <div style={{ fontSize: '14px', opacity: 0.8 }}>
                Package size: {formatBytes(exportStatus.package_size_bytes)}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '8px' }}>
              Export ID
            </div>
            <div style={{
              padding: '10px',
              background: 'var(--body-bg)',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '14px'
            }}>
              {exportStatus.export_id}
            </div>
          </div>

          {exportStatus.expires_at && (
            <div style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              marginBottom: '20px',
              padding: '12px',
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '4px'
            }}>
              <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
              Expires: {new Date(exportStatus.expires_at).toLocaleString()}
            </div>
          )}

          <button
            onClick={downloadPackage}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Download size={20} />
            Download Package
          </button>

          <button
            onClick={() => setExportStatus(null)}
            style={{
              width: '100%',
              padding: '12px',
              background: 'transparent',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '12px'
            }}
          >
            Export Another
          </button>
        </div>
      )}

      <div style={{
        marginTop: '30px',
        padding: '20px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '8px'
      }}>
        <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>What&apos;s Included?</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-muted)' }}>
          <li>User metadata</li>
          <li>Provider configuration</li>
          <li>All resources (compute, database, storage, AI, cache)</li>
          <li>Installed skills and execution history</li>
          <li>OpenClaw deployments</li>
        </ul>
      </div>
    </div>
  )
}
